import { useApolloClient, useLazyQuery } from "@apollo/client"
import { MenuItem } from "@blueprintjs/core"
import { cloneDeep, get, pick, set } from "lodash-es"
import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import CustomButton from "components/Atoms/CustomButton"
import CustomDateInput from "components/Atoms/CustomDateInput"
import CustomSpinner from "components/Atoms/CustomSpinner"
import StringInput from "components/Atoms/StringInput"
import Error from "components/Common/Error"
import Authorization from "components/Molecules/Authorization"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FileUpload from "components/Molecules/FileUpload"
import FormController from "components/Molecules/FormController"
import FormLayout from "components/Molecules/FormLayout"
import MutationOptions from "components/Organisms/MutationOptions"
import UserSuggest from "components/Organisms/UserSuggest"
import { KYCS } from "graphql/selector.queries.gql"
import { VEHICLE_LIST_FRAG } from "graphql/vehicle/vehicle.fragments.gql"
import { ADD_UPDATE_VEHICLE_DRIVER } from "graphql/vehicle/vehicle.mutations.gql"
import { VEHICLE_DRIVER_DETAILS } from "graphql/vehicle/vehicle.queries.gql"
import useErrors from "hooks/useErrors"
import useIdParam from "hooks/useIdParam"
import vehicleDriverModel from "models/organisms/vehicle.driver.model"
import AppToaster from "utils/AppToaster"
import { remove } from "utils/ds.utils"
import { removeTypename } from "utils/objectUtils"
const VehicleDriverRegistration = () => {
  const { state: locationState } = useLocation()
  const state = locationState as any
  const navigate = useNavigate()
  const id = useIdParam()

  const [driverDetails, changeDriverDetails] = useState<any>({})
  const client = useApolloClient()

  const vehicleId = state?.id
  const registrationNumber = state?.registrationNumber

  const parsedVariables = useMemo(() => {
    return {
      ...pick(driverDetails, [
        "id",
        "firstName",
        "lastName",
        "number",
        "licenceDetail",
      ]),
      vehicleId,
      kycDetails: driverDetails?.kycDetails
        ?.filter(k => k.kycType)
        ?.map(k => ({
          ...k,
          kycType: k?.kycType?.id,
        })),
    }
  }, [driverDetails, vehicleId])

  const [errors] = useErrors({
    schema: vehicleDriverModel,
    state: parsedVariables,
  })
  const [getUserDetail, { loading, error }] = useLazyQuery(
    VEHICLE_DRIVER_DETAILS,
    {
      onCompleted({ userDetails: ud }) {
        const kyc = ud.kycDetails

        changeDriverDetails(
          removeTypename({
            ...ud,
            licenceDetail: ud?.licenceDetails?.[0],
            kycDetails: kyc.length ? kyc : [{}],
          })
        )
      },
    }
  )
  useEffect(() => {
    if (id && !driverDetails.id) {
      getUserDetail({ variables: { id } })
    }
  }, [id, driverDetails.id, getUserDetail])
  if (error) return <Error />
  if (loading) return <CustomSpinner />

  const type = get(state, "vehicleType", {})
  const showToast = data => {
    const driver = get(data, "addUpdateVehicleDriver.driver")
    AppToaster.show({
      message: `Driver ${driver.firstName} assigned to vehicle ${registrationNumber}`,
      intent: "success",
    })
  }
  const showError = error => {
    AppToaster.show({
      message: error.message,
      intent: "danger",
    })
  }
  const handleChange = (path, value) => {
    if (driverDetails.id) {
      if (path === "firstName" || path === "lastName" || path === "number") {
        AppToaster.show(
          {
            message: <span>Unable to change basic details of the user.</span>,
            timeout: 10000,
            intent: "warning",
            icon: "warning-sign",
          },
          "unauthorized"
        )
        return
      }
    }
    const driverDetailsCopy = cloneDeep(driverDetails)
    set(driverDetailsCopy, path, value)
    changeDriverDetails(driverDetailsCopy)
  }

  return (
    <FormLayout type="Driver" id={id}>
      <Authorization required={["vt_add_vehicle", "vt_update_vehicle"]} any>
        {({ disabled }) => {
          return (
            <>
              <div className="flex items-baseline">
                <h5 className="mr-6 text-2xl">
                  <span className="text-muted">Vehicle - </span>{" "}
                  {registrationNumber}{" "}
                  <span className="text-muted">
                    ({`${type.name} - ${type.category}`})
                  </span>
                </h5>
              </div>
              <hr className="mt-0" />
              <UserSuggest
                label={
                  <span>
                    Populate <span className="vt-warning">this driver</span>{" "}
                    from the list of existing users
                  </span>
                }
                className="mb-5"
                disabled={disabled}
                onChange={user => {
                  if (user?.id) {
                    getUserDetail({
                      variables: { id: user?.id },
                    })
                  } else {
                    changeDriverDetails({})
                  }
                }}
              />
              <FormController
                errors={errors.details}
                source={driverDetails}
                onChange={handleChange}
                disabled={disabled}
                disableAll={true}
              >
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <StringInput
                      label="First Name"
                      path="firstName"
                      required
                    ></StringInput>
                  </div>
                  <div className="col-span-3">
                    <StringInput
                      label="Last Name"
                      path="lastName"
                      required
                    ></StringInput>
                  </div>
                  <div className="col-span-3">
                    <StringInput
                      label="Phone Number"
                      helperText="eg +919999999999"
                      path="number"
                    />
                  </div>
                </div>

                <h6 className="mt-6 vt-section-header">
                  KYC Details{" "}
                  <CustomButton
                    icon="plus"
                    intent="success"
                    minimal
                    onClick={e => {
                      changeDriverDetails(prev => ({
                        ...prev,
                        kycDetails: prev?.kycDetails?.concat({}),
                      }))
                    }}
                  />
                </h6>
                <hr className="mt-0" />
                {driverDetails?.kycDetails?.map((d, idx) => {
                  const base = `kycDetails.${idx}.`
                  return (
                    <div key={d?.id ?? idx} className="grid grid-cols-12 gap-4">
                      <div className="col-span-3">
                        <DynamicSelector
                          big
                          required
                          query={KYCS}
                          path={base + "kycType"}
                          label="Type"
                          helperText="eg. Aadhar"
                          itemRenderer={(item, { handleClick, modifiers }) => {
                            return (
                              <MenuItem
                                disabled={
                                  driverDetails?.kycDetails?.find(
                                    k => k?.kycType?.id === item?.id
                                  )
                                    ? true
                                    : false
                                }
                                active={modifiers.active}
                                key={item.id}
                                onClick={handleClick}
                                text={item?.name}
                              />
                            )
                          }}
                        />
                      </div>
                      <div className="col-span-3">
                        <StringInput
                          path={base + "idNumber"}
                          label="Number"
                          helperText="eg. 435678986545"
                        />
                      </div>
                      <div className="col-span-3">
                        <FileUpload
                          big={true}
                          path={base + "photo"}
                          label="Kyc Photo"
                        />
                      </div>
                      <div className="col-span-1 flex justify-center items-start">
                        <CustomButton
                          className="mt-3"
                          icon="trash"
                          intent="danger"
                          minimal
                          onClick={e => {
                            changeDriverDetails(prev => ({
                              ...prev,
                              kycDetails: remove(prev?.kycDetails, idx),
                            }))
                          }}
                        />
                      </div>
                    </div>
                  )
                })}

                <h6 className="mt-6 vt-section-header">Licence Details</h6>
                <hr className="mt-0" />
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <StringInput
                      path="licenceDetail.idNumber"
                      label="Licence Id"
                    />
                  </div>
                  <div className="col-span-3">
                    <CustomDateInput
                      path="licenceDetail.issuedOn"
                      label="Issued On"
                    />
                  </div>
                  <div className="col-span-3">
                    <CustomDateInput
                      path="licenceDetail.validTill"
                      label="Valid Till"
                    />
                  </div>
                  <div className="col-span-3">
                    <FileUpload
                      big
                      label="Licence Photo"
                      path="licenceDetail.photo"
                    />
                  </div>
                </div>
              </FormController>
              <div className="mt-6 flex justify-end">
                <MutationOptions
                  disabled={disabled}
                  mutation={ADD_UPDATE_VEHICLE_DRIVER}
                  variables={{ driver: parsedVariables }}
                  onError={showError}
                  validate={() => {
                    if (errors.latest) {
                      AppToaster.show({
                        intent: "danger",
                        icon: "cross",
                        message: errors.latest,
                      })
                      return false
                    }
                    return true
                  }}
                  update={(cache, result) => {
                    const driver = get(
                      result,
                      "data.addUpdateVehicleDriver.driver"
                    )
                    const vehicle = client.readFragment({
                      id: `VehicleType:${vehicleId}`,
                      fragment: VEHICLE_LIST_FRAG,
                    })

                    client.writeFragment({
                      id: `VehicleType:${vehicleId}`,
                      fragment: VEHICLE_LIST_FRAG,
                      data: {
                        ...vehicle,
                        driver: pick(driver, [
                          "id",
                          "fullName",
                          "number",
                          "__typename",
                        ]),
                      },
                    })
                  }}
                  onSave={data => {
                    showToast(data)
                    navigate(-1)
                  }}
                  onSaveAndAdd={data => {
                    showToast(data)
                    changeDriverDetails({})
                  }}
                  onSaveAndEdit={data => {
                    showToast(data)
                  }}
                />
              </div>
            </>
          )
        }}
      </Authorization>
    </FormLayout>
  )
}

export default VehicleDriverRegistration
